import { Banknote, PiggyBank, Pencil, Receipt, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";

import { EditClientMonthBudgetPopup } from "@/components/ui/v1/popup/EditClientMonthBudgetPopup";
import { Button } from "@/components/ui/v2/button";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import { usePopup } from "@/lib/hooks/usePopup";
import { formatCurrency } from "@/utils/valueFormat";

interface BudgetTileRowProps {
  allocatedBudget?: number;
  availableBudget?: number;
  clientId: string;
  currentBudget?: number;
  isLoading?: boolean;
  monthlyBudget?: number;
  onEditSuccess?: () => void;
}

const BudgetTileRow = ({
  allocatedBudget,
  availableBudget,
  clientId,
  currentBudget,
  isLoading = false,
  monthlyBudget,
  onEditSuccess,
}: BudgetTileRowProps) => {
  const { t } = useTranslation();
  const { openPopup, resetPopup } = usePopup();

  const handleEditClick = () => {
    openPopup(
      <EditClientMonthBudgetPopup
        budget={monthlyBudget || 0}
        clientId={clientId}
        onSuccess={() => {
          onEditSuccess?.();
          resetPopup();
        }}
      />
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        variant="gradient"
        icon={<Wallet className="size-5 text-primary" />}
        isLoading={isLoading}
        label={`${t("common.monthly")} ${t("common.budget")}`}
        value={formatCurrency(monthlyBudget ?? 0)}
        action={
          <Button
            aria-label={`Edit ${t("common.monthly")} ${t("common.budget")}`}
            size="icon-sm"
            variant="ghost"
            onClick={handleEditClick}
          >
            <Pencil className="size-4" />
          </Button>
        }
      />
      <MetricCard
        variant="gradient"
        icon={<Receipt className="size-5 text-primary" />}
        label={`${t("common.allocated")} ${t("common.budget")}`}
        value={formatCurrency(allocatedBudget ?? 0)}
      />
      <MetricCard
        variant="gradient"
        icon={<Banknote className="size-5 text-primary" />}
        label={`${t("common.allocable")} ${t("common.budget")}`}
        value={formatCurrency(availableBudget ?? 0)}
      />
      <MetricCard
        variant="gradient"
        icon={<PiggyBank className="size-5 text-primary" />}
        label={`${t("common.rest")} ${t("common.budget")}`}
        value={formatCurrency(currentBudget ?? 0)}
      />
    </div>
  );
};

export default BudgetTileRow;
